import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useDataLayer } from '@/composables/useDataLayer.js';
import defaultRoutes from '@/router/routes.default.js';
import { aanvullendeDekkingenStore } from '@/stores/aanvullendedekkingen.js';
import { formStore } from '@/stores/form.js';
import { verzekeringStore } from '@/stores/verzekering.js';
import { voertuigStore } from '@/stores/voertuig.js';

export function useRoutingButtonActions() {
  const router = useRouter();
  const form = formStore();
  const voertuigen = voertuigStore();
  const verzekering = verzekeringStore();
  const aanvullendeDekkingen = aanvullendeDekkingenStore();
  const { addDataLayer } = useDataLayer();
  const route = useRoute();
  const { sortedRoutes } = storeToRefs(form);
  const allRoutes = router.getRoutes();

  // Filter the default routes out of all routes so that we get only the 'funnel routes'
  const funnelRoutes = allRoutes.filter((route) => {
    return !defaultRoutes.some((droute) => droute.name === route.name);
  });

  const totalRoutes = computed(() => {
    return funnelRoutes.length;
  });

  const backButtonVisible = computed(() => {
    return route.path !== '/' && route.name !== 'bedankt';
  });

  // Vind de huidige route index in de form.sortedRoutes array
  const currentRouteIndex = computed(() => (Array.isArray(sortedRoutes.value) ? sortedRoutes.value.indexOf(route.name) : 0));

  const routerIndexCheck = (newRouteIndex) => {
    // if there is no additional coverage page, dont go two steps back
    if (router.hasRoute('aanvullend')) {
      // if there is no additional coverage, go back an additional page
      newRouteIndex -= route.meta?.stepKey === 'almost-insured' && !aanvullendeDekkingen.hasAlleAanvullendeDekkingen ? 1 : 0;
    }

    // if the vehicle is a WA vehicle, go back an additional page if page is available
    if (router.hasRoute('AanvullendeInformatie') && route.meta?.stepKey === 'premium-overview') {
      newRouteIndex -= verzekering.getDekking('Voertuig') === 'wa' ? 1 : 0;
    }

    return newRouteIndex;
  };

  const backButtonAction = () => {
    // Bereken de index van de vorige route
    let newRouteIndex = routerIndexCheck(currentRouteIndex.value - 1);

    newRouteIndex = Math.max(0, newRouteIndex);
    router.push({ name: destinationRoute(newRouteIndex) });
  };

  const destinationRoute = (newRouteIndex) => {
    return form.sortedRoutes[newRouteIndex];
  };

  const submitButtonAction = async () => {
    // Bereken de index van de volgende route
    let nextRouteIndex = currentRouteIndex.value + 1 > totalRoutes.value ? currentRouteIndex.value : currentRouteIndex.value + 1;
    // sorteer de voordeelpakket routes
    switch (route.meta.nextStepSubmitAction) {
      case 'updaten-indicatie-premies':
        await form.updatenIndicatiePremies().then(() => {
          router.push({ name: destinationRoute(nextRouteIndex) });
        });
        break;
      case 'updaten-aanvullende-informatie':
        if (verzekering.getDekking('Voertuig') === 'wa') {
          nextRouteIndex += 1;
          await form.updatenPremies().then(() => {
            router.push({ name: destinationRoute(nextRouteIndex) });
          });
        } else {
          await form.doUpdate().then(() => {
            router.push({ name: destinationRoute(nextRouteIndex) });
          });
        }
        break;
      case 'updaten-premies':
        await form.updatenPremies().then(() => {
          router.push({ name: destinationRoute(nextRouteIndex) });
        });
        break;
      case 'updaten-aanvullende-dekkingen':
        await form.updatenAanvullendedekkingen().then(() => {
          // If there are no aanvullendeDekkingen add + 1 to routeIndex
          nextRouteIndex += !aanvullendeDekkingen.hasAlleAanvullendeDekkingen ? 1 : 0;
          router.push({ name: destinationRoute(nextRouteIndex) });
        });
        break;
      case 'update-and-fetch-vragen':
        await form.doUpdate().then(async () => {
          if (!voertuigen.type_id_correct) {
            verzekering.resetVerzekering('Voertuig');
            await router.push({ name: destinationRoute(2) });
            voertuigen.type_id_correct = true;
            return;
          }
          await form.fetchVragen().then(() => {
            router.push({ name: destinationRoute(nextRouteIndex) });
          });
        });
        break;
      case 'updaten-submit-pakket':
        await form
          .updatenSubmitPakket()
          .then(() => {
            router.push({ name: destinationRoute(nextRouteIndex) });
          })
          .catch(() => {});
        break;
      case 'request-insurance':
        await form
          .requestInsurance()
          .then(() => {
            addDataLayer('purchase', nextRouteIndex);

            router.push({ name: destinationRoute(nextRouteIndex) });
          })
          .catch(() => {});
        break;
      case 'request-payment':
        await form
          .requestPayment()
          .then(() => {
            addDataLayer('purchase', nextRouteIndex);
          })
          .catch(() => {});
        break;
      case 'is-voordeelpakket':
        if (destinationRoute(nextRouteIndex) === 'kies-premie') {
          await form.updatenPremies().then(() => {
            router.push({ name: destinationRoute(nextRouteIndex) });
          });
        } else {
          await form.doUpdate().then(() => {
            router.push({ name: destinationRoute(nextRouteIndex) });
          });
        }
        break;
      default:
        await form.doUpdate().then(() => {
          router.push({ name: destinationRoute(nextRouteIndex) });
        });
    }
  };

  return {
    totalRoutes,
    backButtonVisible,
    backButtonAction,
    submitButtonAction,
    currentRouteIndex,
  };
}
